import * as React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Check, ChevronsUpDown, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface MultiSelectOption {
  value: string;
  label: string;
  description?: string;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
}

export function MultiSelect({
  options,
  selected,
  onChange,
  placeholder = 'Selectionner...',
  searchPlaceholder = 'Rechercher...',
  emptyMessage = 'Aucun resultat',
  className,
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const inputRef = React.useRef<HTMLInputElement>(null);

  const selectedOptions = React.useMemo(
    () => options.filter((o) => selected.includes(o.value)),
    [options, selected]
  );

  const filteredOptions = React.useMemo(() => {
    let filtered = options;
    if (search.trim()) {
      const s = search.toLowerCase();
      filtered = filtered.filter(
        (o) =>
          o.label.toLowerCase().includes(s) ||
          o.value.toLowerCase().includes(s) ||
          o.description?.toLowerCase().includes(s)
      );
    }
    // When not searching, show selected first
    if (!search.trim()) {
      const selectedSet = new Set(selected);
      return [...filtered].sort((a, b) => {
        const aSelected = selectedSet.has(a.value) ? 0 : 1;
        const bSelected = selectedSet.has(b.value) ? 0 : 1;
        if (aSelected !== bSelected) return aSelected - bSelected;
        return a.label.localeCompare(b.label);
      });
    }
    return filtered;
  }, [options, search, selected]);

  const toggle = (value: string) => {
    onChange(
      selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value]
    );
  };

  const remove = (value: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(selected.filter((v) => v !== value));
  };

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'flex min-h-12 w-full items-center justify-between',
            'bg-muted/50 border-0 border-b-2 border-border rounded-t-lg rounded-b-none',
            'px-4 pt-3 pb-2 text-sm',
            'hover:bg-muted/70',
            'focus:bg-muted/70 focus:border-b-primary focus:outline-none',
            'disabled:cursor-not-allowed disabled:opacity-50',
            className
          )}
        >
          <div className="flex flex-wrap gap-1 flex-1 mr-2">
            {selectedOptions.length === 0 ? (
              <span className="text-muted-foreground/60">{placeholder}</span>
            ) : (
              selectedOptions.map((option) => (
                <span
                  key={option.value}
                  className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary border border-primary/20 px-2.5 py-0.5 text-xs font-semibold"
                >
                  {option.label}
                  <X
                    className="h-3 w-3 cursor-pointer hover:text-destructive"
                    onClick={(e) => remove(option.value, e)}
                  />
                </span>
              ))
            )}
          </div>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverPrimitive.Trigger>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={4}
          className={cn(
            'z-50 w-[var(--radix-popover-trigger-width)] rounded-lg border bg-popover shadow-md',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          )}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            inputRef.current?.focus();
          }}
        >
          {/* Search */}
          <div className="flex items-center border-b px-3">
            <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
            <input
              ref={inputRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={searchPlaceholder}
              className="flex h-10 w-full bg-transparent px-2 text-sm outline-none placeholder:text-muted-foreground/60"
            />
            {search && (
              <X
                className="h-4 w-4 shrink-0 cursor-pointer text-muted-foreground hover:text-foreground"
                onClick={() => setSearch('')}
              />
            )}
          </div>

          {/* Options list */}
          <div className="max-h-[300px] overflow-y-auto p-1">
            {filteredOptions.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                {emptyMessage}
              </p>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = selected.includes(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => toggle(option.value)}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm cursor-pointer',
                      'hover:bg-muted/50',
                      isSelected && 'bg-primary/5'
                    )}
                  >
                    <div
                      className={cn(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border',
                        isSelected
                          ? 'bg-primary border-primary text-primary-foreground'
                          : 'border-muted-foreground/30'
                      )}
                    >
                      {isSelected && <Check className="h-3 w-3" />}
                    </div>
                    <div className="flex-1 text-left">
                      <span className="font-medium">{option.label}</span>
                      {option.description && (
                        <span className="text-xs text-muted-foreground ml-2">
                          {option.description}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Footer with count */}
          {selected.length > 0 && (
            <div className="border-t px-3 py-2 text-xs text-muted-foreground">
              {selected.length} selectionne{selected.length > 1 ? 's' : ''}
            </div>
          )}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
